import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatcher } from 'src/redux/store';
import { markChequeSettled } from 'src/redux/slices/cheque-factoring';

import FormProvider from 'src/components/hook-form';

import { IChequeItem } from 'src/types/cheque';

interface IMarkSettledDialog {
  cheque: IChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function MarkSettledDialog({ cheque, isOpen, onClose, onSuccess }: IMarkSettledDialog) {
  const dispatch = useAppDispatcher();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        settledDate: Yup.date().required('Settled date is required'),
      }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { settledDate: new Date() },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        markChequeSettled({
          chequeId: cheque._id,
          settledDate: new Date(data.settledDate),
        })
      ).unwrap();

      enqueueSnackbar('Cheque marked as settled with LOLC');
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error' });
      } else {
        enqueueSnackbar(e?.message || 'Failed to mark cheque as settled', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Mark Settled With LOLC</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Confirm the date we settled cheque <strong>{cheque.chequeNumber}</strong>&apos;s
            returned amount with LOLC.
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="settledDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Settled Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default MarkSettledDialog;
